import React from 'react';
import { render, screen, fireEvent, act } from '@testing-library/react';

// Mock child components
jest.mock('../BigPlayer', () => {
  const ReactMock = require('react');
  return ReactMock.forwardRef(({ item }, ref) => {
    ReactMock.useImperativeHandle(ref, () => ({ play: jest.fn() }));
    return <div data-testid="big-player">{item?.title}</div>;
  });
});

jest.mock('../ListItem', () => ({ item, onPress }) => (
  <div data-testid={`list-item-${item.id}`} onClick={() => onPress(item.id)}>
    {item.title}
  </div>
));

// Mock the API module
jest.mock('../../api', () => ({
  getPodcast: jest.fn(),
}));

import Podcast from '../Podcast';
import API from '../../api';

// Mock jQuery globally
beforeEach(() => {
  global.$ = jest.fn(() => ({
    offset: () => ({ top: 100 }),
    css: () => 'static',
    outerHeight: () => 60,
    animate: jest.fn((_, __, ___, cb) => cb && cb()),
  }));
});

const makePodcast = (itemCount = 3) => ({
  title: 'Test Podcast',
  items: Array.from({ length: itemCount }, (_, i) => ({
    id: i + 1,
    title: `Episode ${i + 1}`,
  })),
});

const defaultProps = {
  item: { settings: { player: { podcast_uuid: 'abc-123' } } },
  protocol: 'https',
  host: 'example.com',
};

describe('Podcast', () => {
  beforeEach(() => {
    jest.useFakeTimers();
    fetch.resetMocks();
  });

  afterEach(() => {
    jest.useRealTimers();
  });

  it('renders nothing when podcast data has not loaded', () => {
    API.getPodcast.mockReturnValue(new Promise(() => {})); // never resolves
    const { container } = render(<Podcast {...defaultProps} />);
    act(() => jest.advanceTimersByTime(100));
    expect(container.innerHTML).toBe('');
  });

  it('renders nothing when item has no podcast_uuid', () => {
    const { container } = render(<Podcast item={{}} protocol="https" host="example.com" />);
    act(() => jest.advanceTimersByTime(100));
    expect(container.innerHTML).toBe('');
  });

  it('fetches podcast and renders BigPlayer + list items', async () => {
    const podcast = makePodcast(3);
    API.getPodcast.mockResolvedValue(podcast);

    render(<Podcast {...defaultProps} />);
    act(() => jest.advanceTimersByTime(100));
    await act(async () => {});

    expect(API.getPodcast).toHaveBeenCalledWith('https', 'example.com', 'abc-123');
    expect(screen.getByTestId('big-player')).toBeInTheDocument();
    expect(screen.getByText('More from Test Podcast')).toBeInTheDocument();
  });

  it('selectItem changes the active player item', async () => {
    const podcast = makePodcast(3);
    API.getPodcast.mockResolvedValue(podcast);

    render(<Podcast {...defaultProps} />);
    act(() => jest.advanceTimersByTime(100));
    await act(async () => {});

    // Click a list item to select it
    const listItem = screen.getByTestId('list-item-2');
    fireEvent.click(listItem);

    // BigPlayer should now show Episode 2
    expect(screen.getByTestId('big-player')).toHaveTextContent('Episode 2');
  });

  it('showMore button appears when items exceed itemsToShow and loads more', async () => {
    const podcast = makePodcast(15);
    API.getPodcast.mockResolvedValue(podcast);

    render(<Podcast {...defaultProps} />);
    act(() => jest.advanceTimersByTime(100));
    await act(async () => {});

    const showMoreBtn = screen.getByText('Show More');
    expect(showMoreBtn).toBeInTheDocument();

    fireEvent.click(showMoreBtn);
    // After clicking, more items should be visible (up to 20 now)
    // The "Show More" button should disappear since 15 items < 20 limit
    expect(screen.queryByText('Show More')).not.toBeInTheDocument();
  });

  it('does not update state after unmount', async () => {
    const podcast = makePodcast(2);
    let resolvePromise;
    API.getPodcast.mockReturnValue(new Promise(r => { resolvePromise = r; }));

    const { unmount } = render(<Podcast {...defaultProps} />);
    act(() => jest.advanceTimersByTime(100));
    unmount();

    // Resolve after unmount — should not throw
    await act(async () => resolvePromise(podcast));
  });
});
